<script setup>
  import { onMounted, computed, ref, watch, nextTick, onBeforeUnmount } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import DataEditor from '@/components/DataEditor.vue'
  import { useStaffStore } from '@/stores/staffStore'
  import { useCompanyStore } from '@/stores/companyStore'
  import { useDialogStore } from '@/stores/dialogStore'

  const router = useRouter()
  const route = useRoute()
  const staffStore = useStaffStore()
  const companyStore = useCompanyStore()
  const dialogStore = useDialogStore()

  const breadcrumbs = [
    { name: 'ダッシュボード', url: '/dashboard' },
    { name: '会社一覧', url: '/company' },
    {
      name: '事業場一覧',
      path: { name: 'branch', params: { companyId: route.params.companyId } },
    },
    {
      name: '従業員一覧',
      path: { name: 'staff', params: { companyId: route.params.companyId } },
    },
    { name: '従業員詳細', url: '' },
  ]

  // data ///////////////////////////////////////
  const selectedBranchId = ref() // 選択された事業場ID
  const refreshCount = ref(0) // データエディタのリフレッシュカウンター
  const dataEditorRef = ref(null) // DataEditorコンポーネントのref

  // データエディタをリフレッシュする関数
  const refreshEditor = () => {
    refreshCount.value++
  }

  // computed ///////////////////////////////////

  // 新規作成かどうかを判定
  const isNew = computed(() => {
    return route.params.staffId === 'new'
  })

  // 選択肢をリアクティブに管理するcomputedプロパティ
  const optionsMap = computed(() => {
    const branchId = selectedBranchId.value

    // 労災保険区分のオプションを取得し、詳細をログ出力
    const laborOptions = companyStore.laborInsuranceTypeOptions(branchId)

    return {
      事業場名: companyStore.branchOptions,
      労災保険区分: laborOptions || [],
    }
  })

  // methods ///////////////////////////////////

  // StaffDetail.vue内のonMountedを修正
  onMounted(async () => {
    // 会社IDを設定
    staffStore.setCurrentCompanyId(route.params.companyId)

    // 事業場一覧を取得
    if (!(await companyStore.getBranches(route.params.companyId))) {
      // 事業場が取得できなかった場合は、前のページに戻る
      router.back()
      return
    }

    // 全事業場の労災保険区分を一括取得
    await companyStore.getAllLaborInsuranceTypes(route.params.companyId)

    if (isNew.value) {
      // 新規作成の場合
      const defaultBranchId = companyStore.branchOptions[0]?.value
      selectedBranchId.value = route.query.branchId ? Number(route.query.branchId) : defaultBranchId
      staffStore.clearDetailData(selectedBranchId.value)
    } else {
      // 編集の場合
      const result = await staffStore.initDetailData(route.params.staffId)
      if (!result) {
        router.back()
        return
      }
      selectedBranchId.value = Number(staffStore.formData.branchId)
    }

    if (!staffStore.initialStaffData) {
      return
    }
    refreshEditor()

    // 新規作成の場合は一括編集ダイアログを開く
    if (isNew.value) {
      // nextTickを使ってコンポーネントが完全にレンダリングされた後に実行
      await nextTick()
      if (dataEditorRef.value && dataEditorRef.value.startEditAll) {
        dataEditorRef.value.startEditAll()
      }
    }
  })

  // onBeforeUnmountを追加して画面を去るときにデータをクリア
  onBeforeUnmount(() => {
    // 画面を去るときに初期データをクリア
    staffStore.clearInitialStaffData()
  })

  // 戻るボタン
  const goBack = () => {
    // 戻る前にデータをクリア
    staffStore.clearInitialStaffData()
    router.back()
  }

  // saveボタン (編集時の各セクション保存用)
  const saveData = async data => {
    // 事業場IDを取得
    const newBranchId = getValueFromData(data, ['事業場情報', '事業場', '事業場名'])
    selectedBranchId.value = newBranchId

    if (isNew.value) {
      // 新規作成の場合はセクションごとの保存はなし、現在のデータをformDataに設定だけする
      staffStore.templateToFormData(data)
      return
    }

    // storeのsaveStaffDataメソッドを呼び出し
    try {
      const message = await staffStore.saveStaffData(data)
      if (message) {
        await dialogStore.showMessageDialog('保存成功', message)
      }
    } catch (error) {
      // エラー発生時のみリフレッシュする
      // 無限ループを防止するため、一旦Vue Updateを待ってからリフレッシュ
      setTimeout(() => {
        refreshEditor()
      }, 0)
    }
  }

  // dataオブジェクトから指定されたパスの値を取得する関数
  const getValueFromData = (data, path) => {
    if (!data || !Array.isArray(path) || path.length === 0) return null

    // staffStoreのfindByPathを使用
    const field = staffStore.findByPath(data, path)
    return field ? field.value : null
  }

  // 新規登録ボタン
  const createStaff = async () => {
    // 新規登録処理を実行
    const message = await staffStore.createStaff()
    if (message) {
      await dialogStore.showMessageDialog('新規登録成功', message)
      // スタッフIDを新しいものにしてURLを更新
      const newStaffId = staffStore.formData.id
      router.replace({
        name: 'staffdetail',
        params: { companyId: route.params.companyId, staffId: newStaffId },
      })
    }
  }

  // 一括編集ダイアログの確定ボタンが押された時の処理
  const onBulkEditConfirm = async () => {
    if (isNew.value) {
      // 新規作成の場合は新規登録処理を実行
      await createStaff()
    }
  }

  // 削除ボタン
  const deleteStaff = async () => {
    // 確認ダイアログを表示
    if (await dialogStore.showConfirmDialog('削除確認', '本当に削除しますか？')) {
      // 削除処理を実行
      const message = await staffStore.deleteStaff()
      if (message) {
        await dialogStore.showMessageDialog('削除成功', message)
        router.push({
          name: 'staff',
          params: { companyId: route.params.companyId },
        })
      }
    }
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <button type="button" class="back" @click="goBack">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h2>{{ isNew ? '従業員新規作成' : '従業員詳細' }}</h2>
      <button v-if="isNew" type="button" class="save-btn" @click="createStaff">新規登録</button>
      <button v-if="!isNew" type="button" class="delete-btn" @click="deleteStaff">削除</button>
    </div>

    <div class="main-body">
      <DataEditor ref="dataEditorRef" v-if="staffStore.initialStaffData && staffStore.initialStaffData.length > 0" :initialData="staffStore.initialStaffData" :optionsMap="optionsMap" :refreshTrigger="refreshCount" :showBulkEdit="isNew" :excludeSections="['退社情報']" :excludeFields="['退社日', '退社理由']" @save="saveData" @bulkEditConfirm="onBulkEditConfirm" />
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />
</template>

<style lang="less" scoped>
  .material-symbols-outlined {
    vertical-align: middle;
  }

  .delete-btn {
    background-color: var(--alert-color); /* アラートカラーを使用 */
    color: #fff;
    border: none;
    border-radius: 15px;
    font-size: 12px;
    width: 68px;
    min-width: 68px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.3s;
    white-space: nowrap;

    &:hover {
      opacity: 0.7;
    }
  }

  .save-btn {
    background-color: #1a7247;
    color: #f7f7f7;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;
    height: 30px;
    font-size: 12px;
    transition: opacity 0.3s;
    &:hover {
      opacity: 0.7;
    }
  }

  .main-header {
    display: flex;
    align-items: center;

    h2 {
      flex-grow: 1;
      margin: 0;
    }
  }
</style>
